import {
    CellPosition,
    ColDef,
    Column,
    ColumnEverythingChangedEvent,
    ColumnResizedEvent,
    ColumnState,
    DragStoppedEvent,
    FilterChangedEvent,
    IRowNode,
    SortChangedEvent,
} from '@ag-grid-community/core';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CurrentStateService } from '@ddv/behaviors';
import { AlertService } from '@ddv/common-components';
import { CrosstalkModalService, CrosstalkService } from '@ddv/crosstalk';
import {
    DataGridComponent,
    CellClickedEventModel,
    GridConfiguration,
    GridState,
    RowSelectionEventModel,
    CustomFilterChangedEvent,
    UserGridColumnOverrides,
} from '@ddv/data-grid';
import { DatasetManagerService, MetadataService } from '@ddv/datasets';
import { UserData, UserEntitlements } from '@ddv/entitlements';
import { ClientDatasetFilterService } from '@ddv/filters';
import { ManagerService } from '@ddv/layout';
import {
    AppWidgetState,
    AUTO_GROUP_COLUMN_ID,
    CLEAR_FILTERS_TO_VIEW_ROWS_MESSAGE,
    crosstalkCheckboxFieldId,
    CustomColumnState,
    GridConfigItem,
    DataUpdateBody,
    EDITABLE_CHECKBOX_FIELD,
    ExportFilteredData,
    FilterPreference,
    getCurrentVisualizationId,
    GridEventType,
    HS_TRADE_ID_FIELD,
    isTFLIncompleteFiles,
    isTradeFileDetails,
    LinkConfiguration,
    MetadataLookup,
    MODE,
    NO_ROWS_TO_SHOW_MESSAGE,
    TFL_DETAILS_AUTO_GROUP_COLUMN_ID,
    TRANSACTION_TYPE_FIELD,
    trebekRealTimeCommentFields,
    WIDGET_LIFECYCLE_EVENT,
    WidgetLifeCycleData,
    WidgetLifecycleEvent,
    ValueConfigItem,
    DashboardPreference,
    NamedQuery,
} from '@ddv/models';
import { FuzzyDatesService } from '@ddv/reference-data';
import { deepCompare, deepClone } from '@ddv/utils';
import { FuzzyDate } from '@hs/ui-core-date';
import { BehaviorSubject, Observable } from 'rxjs';

import { allFrameworkComponents } from './all-framework-components';
import { BaseVisualizationComponent } from '../base/base-visualization.component';
import { SelectedWidgetRelayService } from '../base/selected-widget-relay.service';
import { GridEvent } from './models/grid-event';
import { GridConfigService } from './services/grid-config.service';
import { UserGridColumnOverridesService } from './services/user-grid-column-overrides.service';

@Component({ template: '', standalone: false })
export abstract class BaseGridVisualizationComponent extends BaseVisualizationComponent implements OnInit, OnDestroy {
    @Input() entitlements: UserEntitlements | undefined;
    @Input() user: UserData | undefined;
    @Input() fuzzyDates: FuzzyDate[] | undefined;

    @Output() gridStateUpdated = new EventEmitter<GridEvent>();
    @Output() cellClicked = new EventEmitter<CellClickedEventModel>();
    @Output() detailWidgetClicked = new EventEmitter<{ detailWidgetId: number, clientCode: string }>();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    allFrameworkComponents: Record<string, new (...args: any[]) => any> = allFrameworkComponents;
    isWidgetReadOnly = false;
    gridConfiguration: GridConfiguration | undefined;
    showGrid = false;
    showRowFilter = false;
    isMousedownInside = false;
    isGridReadyObs: Observable<boolean> | undefined;
    rowSearchText = '';
    // The property below should be removed once we complete the migrations
    visualizationId = 0;
    isTFLDetails = false;
    isTFLIncompleteFiles = false;
    isInViewMode = false;
    isCrosstalkGrid = false;

    protected gridState: GridState | undefined;
    protected isGridReady = false;
    protected distinctRowsOnly = false;
    protected widgetFilters: FilterPreference | undefined;
    protected conversableType: string | undefined;
    protected tempColumnOrder: string[] = [];
    protected localConfigName = '';
    protected widgetPrefs: AppWidgetState | undefined;
    protected initialColumnState: CustomColumnState[] = [];
    protected detailWidgetOpened = false;
    protected dashboardId: string | undefined;
    protected missingRequiredParametersMessage: string | undefined;
    @ViewChild(DataGridComponent, { static: false }) protected dataGridComponent: DataGridComponent | undefined;

    private isDataLoading = false;
    private readonly isGridReadySubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private originalData: any[] | undefined;
    private savedFiltersAndSortsApplied = false;
    private isInitialLoad = false;
    private isOverridingInProcess = false;

    constructor(
        protected readonly alertService: AlertService,
        protected clientDatasetFilterService: ClientDatasetFilterService,
        protected elementRef: ElementRef,
        protected crosstalkModalService: CrosstalkModalService, // This service launches ALL modals, not just crosstalk
        protected gridConfigService: GridConfigService,
        metadataService: MetadataService,
        protected crosstalkService: CrosstalkService,
        protected fuzzyDatesService: FuzzyDatesService,
        protected userGridColumnOverridesService: UserGridColumnOverridesService,
        protected readonly currentStateService: CurrentStateService,
        protected readonly manager: ManagerService,
        private readonly selectedWidgetRelayService: SelectedWidgetRelayService,
        protected datasetManagerService: DatasetManagerService,
        protected cdr: ChangeDetectorRef,
    ) {
        super(metadataService);

        this.isGridReadyObs = this.isGridReadySubject.asObservable();
        this.showRowFilter = true;
        this.distinctRowsOnly = false;
    }

    override ngOnInit(): void {
        this.isInitialLoad = true;
        if (this.preferences?.configs?.values && this.isGridConfigItemList(this.preferences.configs.values)) {
            this.initialValues = this.preferences.configs.values;
        }

        super.ngOnInit();
        this.isChartInitialized = false;
        this.dashboardId = this.manager.getCurrentDashboardId();

        this.selectedWidgetRelayService.isWidgetGlobal$
            .subscribe({
                next: (isWidgetGlobal) => {
                    this.isWidgetReadOnly = this.isManageWidgetMode() && !!this.entitlements?.hasGlobalEditPartial() && isWidgetGlobal;
                },
            });

        this.widgetPrefs = this.getWidgetPreferences() ?? undefined;
        const queryTypeName = this.getQueryTypeName();
        this.isTFLDetails = isTradeFileDetails(queryTypeName);
        this.isTFLIncompleteFiles = isTFLIncompleteFiles(queryTypeName);
        this.conversableType = this.getConversableType();

        this.subscribeTo(this.selectedWidgetRelayService.clearAllFilters$, ({ id, clearAllFilters }) => {
            if (this.dataGridComponent && id === this.widgetId && clearAllFilters) {
                this.dataGridComponent.clearAllFilters();

                if (!this.isManageWidgetMode() && this.isInViewMode) {
                    const nonDynamicColumns = this.filterOutDynamicColumns();
                    const columns = this.dataGridComponent.getAllColumns().filter((c) => nonDynamicColumns.includes(c.getColId()));
                    this.userGridColumnOverridesService.updateColumnFilter(id, {}, columns, this.initialColumnState, true);
                }
            }
        });

        this.localConfigName = `config::${this.user?.userName}::${this.manager.getCurrentDashboardId()}::${this.widgetId}`;

        this.visualizationId = getCurrentVisualizationId(this.widgetPrefs) ?? 0;
        this.isCrosstalkGrid = this.isAdvancedGrid() && !!this.conversableType;

        this.subscribeTo(this.currentStateService.dashboardModeAndId$, ({ mode }) => {
            this.isInViewMode = !this.isManageWidgetMode() && mode === MODE.VIEW;

            if (this.isInViewMode) {
                this.initialColumnState.forEach((state) => state.agFilterOnViewEditMode = null);
            }

            this.setGridColumnOverrides();
        });

        this.subscribeTo(this.manager.isStackedQuery(this.namedQueryId), (isStackedQuery) => this.isStackedQuery = isStackedQuery);
    }

    onMetadataUpdate(): void {
        if (this.showGrid) {
            return;
        }
        this.setupGrid();
    }

    override onDynamicColumnsUpdate(): void {
        if (this.showGrid && this.preferences) {
            this.gridConfiguration = this.gridConfigService
                .getGridConfiguration(this.preferences, this.widgetId, this.namedQueryId, this.metadata);
            setTimeout(() => this.setGridData(), 0);
        }
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.isGridReady = false;
        this.isChartInitialized = false;
        this.showGrid = false;
    }

    widgetLifeCycleCallBack(eventName: WIDGET_LIFECYCLE_EVENT.DATA_UPDATE, data: DataUpdateBody): void;
    widgetLifeCycleCallBack(eventName: WIDGET_LIFECYCLE_EVENT.INTER_WIDGET_COMMUNICATION, data: WidgetLifecycleEvent): void;
    widgetLifeCycleCallBack(eventName: WidgetLifecycleEvent, data: WidgetLifeCycleData): void;
    widgetLifeCycleCallBack(
        eventName: WidgetLifecycleEvent | WIDGET_LIFECYCLE_EVENT.DATA_UPDATE | WIDGET_LIFECYCLE_EVENT.INTER_WIDGET_COMMUNICATION,
        data: DataUpdateBody | WidgetLifecycleEvent | WidgetLifeCycleData,
    ): void {
        switch (eventName) {
            case WIDGET_LIFECYCLE_EVENT.DATA_UPDATE:
                if (this.dataGridComponent && this.isTFLDetails) {
                    this.dataGridComponent.horizontallyScrollToTheBeginning();
                }
                this.updateDataSource(data as DataUpdateBody);
                this.isDataLoading = false;
                break;
            case WIDGET_LIFECYCLE_EVENT.LOADING_DATA:
                this.isDataLoading = true;
                if (this.isGridReady && this.dataGridComponent) {
                    this.dataGridComponent.hideOverlay();
                }
                break;
            case WIDGET_LIFECYCLE_EVENT.VIEW_RESTORED:
                if (this.isInViewMode) {
                    this.restoreInitialColumnState();
                }

                this.dataGridComponent?.refreshHeader();
                break;
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    widgetLifeCyclePostProcess(_eventName: WIDGET_LIFECYCLE_EVENT, _data: unknown): void {}

    onGridReady(): void {
        this.isGridReady = true;
        this.initColumnState();
        this.initGridColumnOverrides();
        this.onReady();
        if (this.isDataLoading && this.gridConfiguration?.gridOptions) {
            this.dataGridComponent?.hideOverlay();
        }
    }

    onReady(): void {
        this.setGridData();
    }

    setStateFromColumnConfigs(): void {
        this.gridState = this.getGridState();
        this.gridConfigService.initializeGridFiltersAndSorts(this.preferences?.configs?.values as GridConfigItem[], this.gridState);
        this.setGroupSortFromConfig();
        this.fixSortIndicesAfterSortUpdate();
        this.setGridState(this.gridState);
    }

    setGroupSortFromConfig(): void {
        if (this.isAdvancedGrid() && this.preferences?.groupSortIndex != null && this.gridState) {
            this.gridConfigService.initializeGroupSort(this.preferences.groupSort, this.preferences.groupSortIndex, this.gridState);
        }
    }

    updateDataSource(componentData: DataUpdateBody): void {
        this.widgetFilters = componentData.widgetPrefs ? componentData.widgetPrefs.widgetFilters : undefined;

        this.originalData ??= deepClone(componentData.data);

        this.vizData = componentData.data;

        if (componentData.filters?.activeDate && !this.isStackedQuery) {
            this.vizData = this.clientDatasetFilterService.filterData(this.vizData, { activeDate: componentData.filters.activeDate });
        }

        if (this.distinctRowsOnly) {
            this.vizData = this.clientDatasetFilterService.removeDuplicateRows(
                this.vizData,
                this.gridConfiguration?.columnDefinitions.map((column: ColDef) => column.field!));
        }

        this.setLocaleTextMessage();

        if (this.isGridReady) {
            this.setGridData(componentData.widgetPrefs?.hideLoaderAfterFirstDataLoad);
        }
    }

    getExportFilteredData(isCsv = false, onlySelectedRows = false): ExportFilteredData {
        return {
            data: this.gridConfigService.getExportFilteredData(this.dataGridComponent, this.preferences, isCsv, onlySelectedRows),
            summary: {},
        };
    }

    getExportFilteredDataForCSV(): ExportFilteredData {
        const onlySelected = !!this.getSelectedNodes().length;

        return {
            data: this.gridConfigService.getExportFilteredData(this.dataGridComponent, this.preferences, true, onlySelected),
            summary: {},
        };
    }

    getExportFilteredDataGroupers(): string[] {
        return this.gridConfigService.getExportFilteredDataGroupers(this.dataGridComponent);
    }

    getFilteredExportVisibleColumns(): string[] {
        return this.gridConfigService.getFilteredExportVisibleColumns(this.dataGridComponent);
    }

    getExportFullData(): ExportFilteredData {
        return { data: this.gridConfigService.getExportFullData(this.dataGridComponent), summary: {} };
    }

    onFilterChanged(params: FilterChangedEvent): void {
        this.setGridAggregateRow();
        this.gridConfigService.updateColumnFilterFromState(this.widgetId, this.getGridState(), this.preferences);
        this.setColumnsWidthOnFilterChanged(params);

        if (this.shouldUpdateColumnFilter(params)) {
            const nonDynamicColumns = this.filterOutDynamicColumns();
            const columns = params.api.getColumns()?.filter((c) => nonDynamicColumns.includes(c.getColId())) ?? [];
            const filterModel = params.api.getFilterModel();
            this.userGridColumnOverridesService.updateColumnFilter(this.widgetId, filterModel, columns, this.initialColumnState);
        }

        if (this.isManageWidgetMode() || (!this.isOverridingInProcess && !this.isInViewMode)) {
            if (this.initialColumnState.length) {
                this.updateInitialColumnStateFiltersOnFilterChanged(params);
            }
            if (this.isManageWidgetMode() || params.source === 'columnFilter') {
                this.gridStateUpdated.emit(this.getGridStateEvent('FILTER'));
            }
        }

        const rowCount = this.dataGridComponent?.grid.api?.getDisplayedRowCount();

        if (rowCount === 0 && this.vizData.length !== 0 && this.dataGridComponent?.grid?.localeText) {
            this.dataGridComponent.grid.localeText.noRowsToShow = CLEAR_FILTERS_TO_VIEW_ROWS_MESSAGE;
            this.dataGridComponent?.showNoRowsOverlay();
        } else if (rowCount !== 0) {
            this.dataGridComponent?.hideOverlay();
        }
    }

    setupGrid(): void {
        if (!this.preferences) {
            return console.error('cannot setupGrid without a preferences');
        }

        if (this.preferences.configs) {
            this.preferences.configs.values = this.gridConfigService.getUpdatedColumnConfigs(this.preferences, this.metadata);
        }
        this.gridConfiguration = this.gridConfigService.getGridConfiguration(
            this.preferences,
            this.widgetId,
            this.namedQueryId,
            this.metadata);

        if (this.isAdvancedGrid()) {
            this.gridConfiguration.gridOptions.groupDefaultExpanded = this.preferences.groupDefaultExpanded;
        }

        this.showRowFilter = !!this.gridConfiguration.showQuickSearch;
        this.distinctRowsOnly = !!this.gridConfiguration.distinctRowsOnly;

        this.gridConfiguration.gridOptions.onFilterChanged = (event: FilterChangedEvent): void => {
            if (this.isGridReady && this.savedFiltersAndSortsApplied) {
                this.onFilterChanged(event);
            }
        };

        this.gridConfiguration.gridOptions.onSortChanged = (event: SortChangedEvent): void => {
            if (this.isGridReady && this.savedFiltersAndSortsApplied) {
                this.onSortChanged(event);
            }
        };

        this.gridConfiguration.gridOptions.onColumnResized = (event: ColumnResizedEvent): void => {
            this.gridConfigService.columnResizeFormatter(this.elementRef, event);
            if (Object.prototype.hasOwnProperty.call(event, 'finished')) {
                if (this.shouldUpdateColumnWidthOverride(event)) {
                    this.userGridColumnOverridesService.updateColumnWidth(this.widgetId, event);
                }

                this.gridStateUpdated.emit(this.getGridStateEvent('COLUMNS'));
            }
        };

        this.showGrid = true;
    }

    conditionallyFollowLink(event: CellClickedEventModel): void {
        if (!this.preferences?.showHyperlinks) {
            return;
        }

        const column = this.preferences.configs?.values.find((value) => value?.colId === event.column?.colId);
        if (column && this.isGridConfigItem(column) && column.linkType === 'ddvwidget') {
            const detailWidgetId = column.ddvWidget ?? 0;
            const clientCode = event.rowData?.clientCode;
            this.detailWidgetClicked.next({ detailWidgetId, clientCode });
            this.detailWidgetOpened = true;
            return;
        }

        const columnId = event.column.colDef.field;
        if (trebekRealTimeCommentFields.some((c) => c === columnId)) {
            return;
        }

        if (!this.gridConfigService.columnIsExternallyLinked(columnId, event.rowData)) {
            return;
        }

        const linkConfiguration: LinkConfiguration = this.gridConfigService.getExternalLinkConfiguration(columnId, event.rowData);
        if (!this.crosstalkModalService.linkAsAModal(linkConfiguration)) {
            return;
        }

        const skipBuildUrl = linkConfiguration.uri.includes('api-support-tool') ||
            linkConfiguration.uri.startsWith('http') ||
            linkConfiguration.uri.startsWith('//');
        this.crosstalkModalService.openModal(linkConfiguration, event.event, skipBuildUrl);
    }

    onRowSelected(event: RowSelectionEventModel): void {
        const columnDef = event.columnDefs?.[0];
        if (columnDef?.field) {
            this.handleNonLinkCellClick(columnDef, event.selectedRowData[columnDef.field]);
        }
    }

    onRowUnselected(event: RowSelectionEventModel): void {
        const columnDef = event.columnDefs?.[0];
        if (columnDef?.field) {
            this.handleNonLinkCellClick(columnDef, event.selectedRowData[columnDef?.field]);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-explicit-any
    handleNonLinkCellClick(colDef: ColDef, value: any): void {
        if (!this.isMaster || !this.isMousedownInside) {
            return;
        }

        const selectedValues: string[] = this.getSelectedNodes().map((row) => row.data[colDef.field!]);

        this.valueClickedInMaster.emit({
            property: colDef.field!,
            name: colDef.headerName!,
            value,
            selectedValues: [...new Set(selectedValues)],
        });
    }

    setGridState(state?: GridState): void {
        if (state) {
            this.dataGridComponent?.setState(state);
        }
    }

    getGridState(): GridState | undefined {
        return this.dataGridComponent?.getState();
    }

    getGridStateEvent(event: GridEventType, triggeredByUser?: boolean): GridEvent {
        this.gridState = this.getGridState();
        return {
            event,
            configs: this.preferences?.configs?.values as GridConfigItem[] ?? [],
            gridState: this.gridState!,
            triggeredByUser: triggeredByUser ?? (this.isGridReady && this.isChartInitialized),
        };
    }

    onQuickFilterChanged(searchText: string): void {
        this.rowSearchText = searchText;
        if (this.dataGridComponent) {
            this.dataGridComponent.setQuickFilter(this.rowSearchText);
        }
    }

    deselectAllSelected(): void {
        this.isMousedownInside = false;
        this.dataGridComponent?.deselectAllSelected();
    }

    areFiltersApplied(): boolean {
        const filters = this.dataGridComponent?.getFilterModel();
        return !!(filters && Object.keys(filters).length);
    }

    setReorderedColumns(columnIds: string[]): void {
        let reorderedColumns: GridConfigItem[] = [];

        const groupIdx = columnIds.indexOf(AUTO_GROUP_COLUMN_ID);
        if (groupIdx !== -1) {
            columnIds.splice(groupIdx, 1);
        }

        this.preferences?.configs?.values.forEach((value) => {
            const idx = columnIds.indexOf(value.colId);
            if (idx !== -1) {
                reorderedColumns[idx] = value as GridConfigItem;
            }
        });

        reorderedColumns = reorderedColumns.filter((column) => column);

        if (this.preferences?.configs) {
            this.preferences.configs.values = reorderedColumns;
        }
    }

    onDragStopped(event: DragStoppedEvent, columns: ColumnState[] = []): void {
        const columnIds: string[] = columns.map((c) => c.colId);
        const tempColumnOrder = [...this.tempColumnOrder];
        if (this.tempColumnOrder.length) {
            if (tempColumnOrder.indexOf(EDITABLE_CHECKBOX_FIELD) !== -1) {
                tempColumnOrder.splice(0, 1);
            }

            if (tempColumnOrder.indexOf(crosstalkCheckboxFieldId) !== -1) {
                const columnDefs: ColumnState[] = event.api.getColumnState();
                const checkboxColumn = columnDefs.find((col) => col.colId === crosstalkCheckboxFieldId);
                const tempCheckboxColumnIndex = tempColumnOrder.findIndex((colId) => colId === crosstalkCheckboxFieldId);
                if (checkboxColumn?.pinned) {
                    tempColumnOrder.splice(tempCheckboxColumnIndex, 1);
                }
            }

            const groupColumnIndex = tempColumnOrder.findIndex((column) => column === AUTO_GROUP_COLUMN_ID);
            if (groupColumnIndex !== -1) {
                tempColumnOrder.splice(groupColumnIndex, 1);
            }

            this.userGridColumnOverridesService.updateColumnsOrder(this.widgetId, tempColumnOrder);
            this.tempColumnOrder = [];

            this.setReorderedColumns(columnIds);

            this.dataGridComponent?.refreshHeader();
        }
    }

    onColumnEverythingChanged(event: ColumnEverythingChangedEvent): void {
        if (event?.source === 'columnMenu') {
            this.restoreInitialColumnState();
        }
    }

    protected isSubscribedToDashboardFilters(): boolean {
        return !!this.getWidgetPreferences()?.isSubscribedToDashboardFilters;
    }

    protected fixSortIndicesAfterSortUpdate(): void {
        if (!this.gridState?.sortState.length) {
            return;
        }

        const sortState = this.gridState.sortState;
        sortState
            .sort((a, b) => a.sortIndex! - b.sortIndex!)
            .forEach((s, index) => s.sortIndex = index);
    }

    protected getConversableType(): string | undefined {
        return this.getWidgetPreferences()?.namedQuery?.normalizeConversableType();
    }

    protected isMasterWidget(): boolean {
        return !!this.getWidgetPreferences()?.isMaster;
    }

    protected getWidgetPreferences(): AppWidgetState | undefined | null {
        return this.manager.getWidgetPreferences(this.widgetId);
    }

    protected getEditingCells(): CellPosition[] {
        return this.dataGridComponent?.getEditingCells() ?? [];
    }

    protected getSelectedNodes(): IRowNode[] {
        return this.dataGridComponent?.getSelectedNodes() ?? [];
    }

    protected getColumnWidth(overrides: UserGridColumnOverrides[], colId: string): number | null | undefined {
        return this.userGridColumnOverridesService.getColumnWidth(
            overrides,
            colId,
            this.isInViewMode,
            this.initialColumnState,
            !!this.gridConfiguration?.autoSizeColumns);
    }

    protected getGroupColumnWidth(overrides: UserGridColumnOverrides[], groupColumns: Column[]): number | null | undefined {
        return this.userGridColumnOverridesService.getGroupColumnWidth(
            overrides,
            this.isInViewMode,
            this.initialColumnState,
            groupColumns,
            !!this.gridConfiguration?.autoSizeColumns);
    }

    protected onSortChanged(event: SortChangedEvent): void {
        this.gridConfigService.updateColumnSortFromState(this.widgetId, this.getGridState(), this.preferences);
        if (this.shouldUpdateColumnSort()) {
            const nonDynamicColumns = this.filterOutDynamicColumns();
            const columns = event.api.getColumnState()
                .filter((c) => nonDynamicColumns.includes(c.colId) || c.colId === AUTO_GROUP_COLUMN_ID);
            this.userGridColumnOverridesService.updateColumnSort(this.widgetId, columns);
        }
        if (this.isManageWidgetMode() || (!this.isOverridingInProcess && !this.isInViewMode)) {
            this.gridStateUpdated.emit(this.getGridStateEvent('SORT'));
        }
    }

    protected setGridData(isRealtimeUpdate = false): void {
        if (!this.vizData) {
            return;
        }

        this.isChartInitialized = false;
        this.gridConfigService
            .updateDataset(this.preferences?.configs?.values as GridConfigItem[] ?? [], this.namedQueryId, this.vizData);
        let data = deepClone(this.vizData);

        if (this.shouldSortGridDataForTFLDetails()) {
            data = this.sortGridDataForTFLDetails(data);
        }

        if (this.metadata && this.widgetPrefs?.currentVisualization === 'ADVANCED_GRID') {
            this.remapUDFsFromTrebekToData(data);
        }

        this.dataGridComponent?.setRowData(data);
        this.setGridAggregateRow();

        if (!this.savedFiltersAndSortsApplied) {
            this.setStateFromColumnConfigs();
            this.savedFiltersAndSortsApplied = true;
        }

        if (!this.isCrosstalkGrid && !this.isDetailWidget()) {
            this.setColumnFilterAndSort();
        }

        if (!isRealtimeUpdate) {
            this.dataGridComponent?.autoSizeColumnsBasedOnConfig();
            if ((this.isTFLDetails || this.isTFLIncompleteFiles) && this.isAdvancedGrid()) {
                this.deselectAllSelected();
            }
        }

        // Callbacks to update columns are still being called from ag-grid after data is
        // set, but we don't want this to trigger a dirty flag
        setTimeout(() => {
            this.isChartInitialized = true;
            this.isGridReadySubject.next(true);
            this.isInitialLoad = false;
        }, 1000);
    }

    protected setColumnFilterAndSort(): void {
        if (!this.dataGridComponent?.grid?.api || !this.widgetPrefs) {
            return;
        }

        const gridState = this.getGridState();

        if (gridState?.columnState) {
            if (this.isManageWidgetMode()) {
                this.setColumnFilterForManageWidgetMode();
            } else {
                const overrides = this.userGridColumnOverridesService.getCurrentGridColumnOverrides(
                    this.widgetId,
                    this.visualizationId,
                    this.widgetPrefs.currentVisualization,
                );
                this.isOverridingInProcess = true;

                this.userGridColumnOverridesService.setColumnsSort(
                    overrides,
                    this.isInViewMode,
                    gridState.columnState,
                    this.initialColumnState,
                    this.dataGridComponent);

                this.userGridColumnOverridesService.setColumnsFilterModel(
                    overrides,
                    this.isInViewMode,
                    this.initialColumnState,
                    this.dataGridComponent);

                setTimeout(() => this.isOverridingInProcess = false, 150);
            }
        }
    }

    protected setGridAggregateRow(): void {
        if (this.preferences?.showGrandTotal) {
            if (this.preferences?.showTotalAtTop) {
                this.dataGridComponent?.setHeaderGrandTotal();
            } else {
                this.dataGridComponent?.setFooterGrandTotal();
            }
        }
    }

    protected setCheckboxColumnPosition(columns: GridConfigItem[] | ColDef[] | ColumnState[], columnType: string): void {
        this.userGridColumnOverridesService.setCheckboxColumnPosition(
            this.widgetId,
            this.visualizationId,
            columns,
            columnType,
            this.isInViewMode,
            this.widgetPrefs?.currentVisualization,
        );
    }

    protected isGroupedByAllocationGroupId(): boolean {
        const groupColumns = this.dataGridComponent?.getRowGroupColumns() ?? [];
        return groupColumns.length === 1 && groupColumns[0].getColDef().field === TFL_DETAILS_AUTO_GROUP_COLUMN_ID;
    }

    protected filterOutDynamicColumns(): string[] {
        if (!this.metadata || !this.preferences?.configs?.values) {
            return [];
        }
        const { values } = this.preferences.configs;
        const initialColumnIds = this.initialValues.map((v) => v.colId);
        const dynamicColumnNames = this.getDynamicColumnNames(this.metadata);

        return values.filter((v) => !(!initialColumnIds.includes(v.colId) && dynamicColumnNames.includes(v.value ?? ''))).map((v) => v.colId);
    }

    protected setMissingRequiredParametersMessage(namedQuery: NamedQuery, params: DashboardPreference): void {
        this.missingRequiredParametersMessage = this.getMissingRequiredParametersMessage(namedQuery, params);
        if (this.missingRequiredParametersMessage) {
            this.setLocaleTextMessage();
        }
        this.cdr.detectChanges();
    }

    private getMissingRequiredParametersMessage(namedQuery: NamedQuery, params: DashboardPreference): string | undefined {
        if (!namedQuery.parameters) {
            return undefined;
        }
        const additionalFilters = namedQuery.parameters.filter((param) => param.type === 'additional-filter');
        if (!additionalFilters.length) {
            return undefined;
        }

        if (!params.additionalFilters) {
            return `Select ${additionalFilters[0].displayName} to Load Data`;
        }

        const additionalFilterWithNoData = additionalFilters
            .find((filter) => !params.additionalFilters?.find((f) => Object.keys(f)[0] === filter.name));
        if (additionalFilterWithNoData) {
            return `Select ${additionalFilterWithNoData.displayName} to Load Data`;
        }

        return undefined;
    }

    private setLocaleTextMessage(): void {
        if (!this.dataGridComponent?.grid?.localeText) {
            return;
        }

        if (this.missingRequiredParametersMessage) {
            this.dataGridComponent.grid.localeText.noRowsToShow = this.missingRequiredParametersMessage;
        } else if (this.vizData?.length === 0 && (this.datasetManagerService?.getSourceDataCopy()?.length ?? 0) > 0) {
            this.dataGridComponent.grid.localeText.noRowsToShow = CLEAR_FILTERS_TO_VIEW_ROWS_MESSAGE;
        } else {
            this.dataGridComponent.grid.localeText.noRowsToShow = NO_ROWS_TO_SHOW_MESSAGE;
        }
        this.dataGridComponent.hideOverlay();
        this.dataGridComponent.showNoRowsOverlay();
    }

    private getDynamicColumnNames(metadata: MetadataLookup): string[] {
        return Object.keys(metadata).filter((key) => metadata[key].isDynamic).map((key) => key);
    }

    private shouldSetGridColumnOverrides(): boolean {
        return !this.isManageWidgetMode() || !!this.vizData;
    }

    private setGridColumnVisibility(overrides: UserGridColumnOverrides[], column: GridConfigItem): void {
        const visible = this.getColumnVisibility(overrides, column.colId);
        this.dataGridComponent?.setColumnVisible(column.colId, visible);
        column.isHidden = !visible;
    }

    private setAutoGroupColumnWidth(overrides: UserGridColumnOverrides[]): void {
        const isGrouped = this.dataGridComponent?.isRowGroupingOn();
        if (isGrouped) {
            const groupColumns = this.dataGridComponent?.getRowGroupColumns() ?? [];
            const groupWidth = this.getGroupColumnWidth(overrides, groupColumns)!;
            this.dataGridComponent?.setColumnWidth(AUTO_GROUP_COLUMN_ID, groupWidth);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private remapUDFsFromTrebekToData(data: { [key: string]: any }[]): void {
        data.forEach((datum) => {
            const udfKeys = Object.keys(datum).filter((key) => key.startsWith('udf_'));
            const udfMetadata = Object.values(this.metadata!).filter((metadataItem) => metadataItem.name?.startsWith('udf_'));

            udfKeys.forEach((udfKey) => {
                const udfField = udfMetadata.find((udf) => udf.name === udfKey);

                if (udfField) {
                    datum[udfKey] = {
                        name: udfField.displayName,
                        type: udfField.datatype,
                        value: datum[udfKey],
                    };
                }
            });
        });
    }

    private shouldSortGridDataForTFLDetails(): boolean {
        const hsTradeIdColumn = this.dataGridComponent?.getAllColumns().find((c) => c.getColDef().field === HS_TRADE_ID_FIELD);
        return this.isAdvancedGrid() && this.isTFLDetails && !!hsTradeIdColumn && this.isGroupedByAllocationGroupId();
    }

    private setColumnFilterForManageWidgetMode(): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const stringFiltersModel = this.initialColumnState.reduce((filter: { [key: string]: any }, column) => {
            const { agFilterOnViewEditMode, agFilter, colId } = column;

            if (!agFilterOnViewEditMode && agFilter?.includedStrings) {
                agFilter.filterType = 'set';
                agFilter.values = agFilter.values ?? JSON.parse(agFilter.includedStrings);
                filter[colId] = agFilter;
            }

            return filter;
        }, {});

        if (Object.keys(stringFiltersModel).length) {
            const filterModel = this.dataGridComponent!.getFilterModel();
            setTimeout(() => this.dataGridComponent!.setFilterModel({ ...filterModel, ...stringFiltersModel }), 50);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private sortGridDataForTFLDetails(data: any[]): any[] {
        const nonAllocations = data.filter((datum) => !datum[TFL_DETAILS_AUTO_GROUP_COLUMN_ID]).sort((a, b) => {
            return a[HS_TRADE_ID_FIELD] - b[HS_TRADE_ID_FIELD];
        });
        const allocations = data.filter((datum) => datum[TFL_DETAILS_AUTO_GROUP_COLUMN_ID]);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const allocationGroupsMap: Map<string, any[]> = new Map();
        allocations.forEach((allocation) => {
            const allocationRows = allocationGroupsMap.get(allocation[TFL_DETAILS_AUTO_GROUP_COLUMN_ID]);
            if (!allocationRows) {
                allocationGroupsMap.set(allocation[TFL_DETAILS_AUTO_GROUP_COLUMN_ID], [allocation]);
            } else {
                allocationRows.push(allocation);
            }
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const sortedAllocations: any[] = [];
        allocationGroupsMap.forEach((group) => {
            const block = group.splice(group.findIndex((item) => item[TRANSACTION_TYPE_FIELD]), 1)[0];
            group.sort((a, b) => a[HS_TRADE_ID_FIELD] - b[HS_TRADE_ID_FIELD]);
            group.unshift(block);
            sortedAllocations.push(...group);
        });

        return [...sortedAllocations, ...nonAllocations];
    }

    private initColumnState(): void {
        if (!this.initialColumnState.length) {
            const columns = (this.preferences?.configs?.values as GridConfigItem[])?.map((c) => ({ ...c })) ?? [];
            this.initialColumnState = (this.getGridState()?.columnState as CustomColumnState[])
                ?.map((state) => {
                    const initialWidgetState = columns.find((widget) => widget.colId === state.colId);
                    state.sort = initialWidgetState?.agSort?.sort;
                    state.sortIndex = initialWidgetState?.agSortIndex;
                    state.agFilter = initialWidgetState?.agFilter;

                    if (state.colId === AUTO_GROUP_COLUMN_ID) {
                        state.sort = this.preferences?.groupSort;
                        state.sortIndex = this.preferences?.groupSortIndex;

                        // We don't really care which column from the group we'll take
                        // as in manage widgets all of the columns that are included in the group
                        // are saved with the same customWidthWhenGroup
                        const values = this.preferences?.configs?.values;
                        if (values && this.isGridConfigItemList(values)) {
                            const customWidthWhenGroup = values.find((v) => v.rowGroupIndex != null)?.customWidthWhenGroup;
                            state.width = this.preferences?.autoSizeColumns ? undefined : (customWidthWhenGroup ?? undefined);
                        } else {
                            state.width = undefined;
                        }
                    }

                    return state;
                }) || [];
        }
    }

    private setOverridedGridState(columns: GridConfigItem[], gridState: GridState): void {
        const columnIds = columns.map((c) => c.colId);
        const initialColumnIds = this.initialColumnState.map((c) => c.colId);

        if (!deepCompare(columnIds, initialColumnIds) || !this.isInViewMode || this.isAdvancedGrid()) {
            const updatedColumns = this.gridConfigService.orderColumnsInColumnState([...gridState.columnState], columns);
            gridState.columnState = [...updatedColumns];
            this.setGridState(gridState);
        }
    }

    // Some of the overrides need to be set before the grid is filled with data
    // that is why this method exists
    // tried to set them in getGridColumn() in grid config service
    // but that way we screw the initialColumnState
    private initGridColumnOverrides(): void {
        if (this.isManageWidgetMode() ||
            !this.dataGridComponent?.grid?.api ||
            !this.widgetPrefs) {
            return;
        }

        let columns = this.preferences?.configs?.values && this.isGridConfigItemList(this.preferences.configs.values) ?
            this.preferences?.configs?.values ?? [] :
            [];
        const gridState = this.getGridState();

        if (gridState?.columnState) {
            const overrides = this.userGridColumnOverridesService.getCurrentGridColumnOverrides(
                this.widgetId,
                this.visualizationId,
                this.widgetPrefs.currentVisualization,
            );

            this.isOverridingInProcess = true;

            columns = this.getColumnsOrder(overrides, columns);

            if (this.isAdvancedGrid()) {
                columns = this.getGroupColumnsOrder(overrides, columns);
            }

            columns.forEach((column) => {
                const visible = this.getColumnVisibility(overrides, column.colId);
                this.dataGridComponent?.setColumnVisible(column.colId, visible);
                column.isHidden = !visible;

                if (column.colId !== AUTO_GROUP_COLUMN_ID) {
                    this.setColumnWidth(column, overrides);
                }
            });

            if (this.isAdvancedGrid()) {
                this.manager.sendMessageToExistingWidget(this.widgetId, WIDGET_LIFECYCLE_EVENT.GROUP_COLUMNS_ORDER_CHANGED);
            }
            this.setOverridedGridState(columns, gridState);

            const isGrouped = this.dataGridComponent.isRowGroupingOn();
            if (isGrouped) {
                const groupColumns = this.dataGridComponent.getRowGroupColumns() ?? [];
                const groupWidth = this.getGroupColumnWidth(overrides, groupColumns)!;
                this.dataGridComponent.setColumnWidth(AUTO_GROUP_COLUMN_ID, groupWidth);
            }

            this.dataGridComponent.moveColumn(AUTO_GROUP_COLUMN_ID, 0);
        }
    }

    private restoreInitialColumnState(): void {
        this.userGridColumnOverridesService.deleteGridColumnOverrides(this.widgetId, this.initialColumnState).subscribe(() => {
            this.setColumnFilterAndSort();
            this.dataGridComponent?.applyColumnState({ state: this.initialColumnState, applyOrder: true });
        });
    }

    private setGridColumnOverrides(): void {
        if (!this.dataGridComponent?.grid?.api || !this.shouldSetGridColumnOverrides()) {
            return;
        }

        let columns = this.preferences?.configs?.values ?? [];
        const gridState = this.getGridState();

        if (gridState?.columnState) {
            const overrides = this.userGridColumnOverridesService.getCurrentGridColumnOverrides(
                this.widgetId,
                this.visualizationId,
                this.widgetPrefs?.currentVisualization,
            );
            this.isOverridingInProcess = true;

            columns = this.getColumnsOrder(overrides, columns as GridConfigItem[]);

            if (this.isAdvancedGrid()) {
                columns = this.getGroupColumnsOrder(overrides, columns as GridConfigItem[]);
            }

            if (this.isCrosstalkGrid) {
                this.setCheckboxColumnPosition(columns as GridConfigItem[], 'ConfigItem');
            }

            columns.forEach((column) => {
                this.setGridColumnVisibility(overrides, column as GridConfigItem);

                if (column.colId !== AUTO_GROUP_COLUMN_ID) {
                    this.setColumnWidth(column as GridConfigItem, overrides);
                }
            });

            if (this.isAdvancedGrid()) {
                this.manager.sendMessageToExistingWidget(this.widgetId, WIDGET_LIFECYCLE_EVENT.GROUP_COLUMNS_ORDER_CHANGED);
            }
            this.setOverridedGridState(columns as GridConfigItem[], gridState);

            this.setAutoGroupColumnWidth(overrides);

            this.userGridColumnOverridesService.setColumnsSort(
                overrides,
                this.isInViewMode,
                gridState.columnState,
                this.initialColumnState,
                this.dataGridComponent);

            this.userGridColumnOverridesService.setColumnsFilterModel(
                overrides,
                this.isInViewMode,
                this.initialColumnState,
                this.dataGridComponent);

            this.dataGridComponent.moveColumn(AUTO_GROUP_COLUMN_ID, 0);

            setTimeout(() => this.isOverridingInProcess = false, 150);
        }
    }

    private setColumnWidth(column: GridConfigItem, overrides: UserGridColumnOverrides[]): void {
        const width = this.getColumnWidth(overrides, column.colId);
        this.dataGridComponent?.setColumnWidth(column.colId, width!);
        column.width = width!;
    }

    private shouldUpdateColumnWidthOverride(event: ColumnResizedEvent): boolean {
        const { column, finished, source } = event;

        if (this.isManageWidgetMode() || !this.isInViewMode || !column || !finished || source !== 'uiColumnResized') {
            return false;
        }

        const colId = column.getColDef().colId;

        if (!colId) {
            return false;
        }

        return !this.isDetailWidget() && !this.isDynamicColumn(colId);
    }

    private shouldUpdateColumnSort(): boolean {
        return this.isInViewMode &&
            !this.isManageWidgetMode() &&
            !this.isInitialLoad &&
            !this.isOverridingInProcess;
    }

    private shouldUpdateColumnFilter(params: CustomFilterChangedEvent): boolean {
        if (!this.isInViewMode || this.isManageWidgetMode()) {
            return false;
        }

        if (params.source === 'api') {
            return false;
        }

        if (params.source === 'columnFilter' && params.columns[0].getColDef().filter === 'customColumnFilterComponent' && !params.isCustomFilter) {
            return false;
        }

        return true;
    }

    private setColumnsWidthOnFilterChanged(params: FilterChangedEvent): void {
        params.columns.forEach((column) => {
            const colId = column.getColId();
            const headerMinWidth = this.getColumnMinWidth(colId);
            if (column.getMinWidth() !== headerMinWidth) {
                const columnDefs = params.api.getColumnDefs() ?? [];
                const columnDef = columnDefs.find((c: ColDef) => c.colId === colId);
                if (columnDef) {
                    // minWidth is NOT a valid agGrid ColDef property
                    (columnDef as ColDef).minWidth = headerMinWidth;
                    params.api.setGridOption('columnDefs', columnDefs);
                }
            }
            if (headerMinWidth > column.getActualWidth()) {
                params.api.setColumnWidths([{ key: colId, newWidth: headerMinWidth }]);
            }
        });
    }

    private getColumnMinWidth(colId?: string): number {
        const items: number = this.elementRef.nativeElement.querySelectorAll(`[col-id="${colId}"] [data-ref="eLabel"]>:not(.ag-hidden)`).length;
        if (!colId || !items) {
            return 50;
        }
        // The logic for 19 - min icon width is 12px, and we have 7px left margin (sort, filter and hamburger menu icons)
        // +12 - because we have a padding of the header label from the left which is 12px (eLabel)
        const isCommentCounterColumn = this.elementRef.nativeElement.querySelector(`[col-id="${colId}"] [data-ref="eLabel"] .hs-icon-comment`);
        return (items * 19) + (isCommentCounterColumn ? 20 : 12);
    }

    private getColumnsOrder(overrides: UserGridColumnOverrides[], columns: GridConfigItem[]): GridConfigItem[] {
        return this.userGridColumnOverridesService.getColumnsOrder(
            overrides,
            this.isInViewMode,
            columns,
            this.initialColumnState);
    }

    private getGroupColumnsOrder(overrides: UserGridColumnOverrides[], columns: GridConfigItem[]): GridConfigItem[] {
        return this.userGridColumnOverridesService.getGroupColumnsOrder(
            overrides,
            this.isInViewMode,
            columns,
            this.initialColumnState);
    }

    private getColumnVisibility(overrides: UserGridColumnOverrides[], colId: string): boolean {
        return this.userGridColumnOverridesService.getColumnVisibility(
            overrides,
            this.initialColumnState,
            colId,
            this.isInViewMode);
    }

    private updateInitialColumnStateFiltersOnFilterChanged(filterModel: FilterChangedEvent): void {
        this.initialColumnState.forEach((column) => {
            if (this.isFilterChangedFromColumnMenu(filterModel, column.colId)) {
                column.agFilterOnViewEditMode = this.dataGridComponent?.getFilterModel()?.[column.colId] ?? {};
            }
        });
    }

    private isFilterChangedFromColumnMenu(filterModel: CustomFilterChangedEvent, columnId: string): boolean {
        if (filterModel.source === 'api') {
            return false;
        }

        if (filterModel.source === 'columnFilter' && filterModel.columns[0].getColId() !== columnId) {
            return false;
        }

        if (filterModel.source === 'columnFilter' && filterModel.columns[0].getColDef().filter === 'customColumnFilterComponent' && !filterModel.isCustomFilter) {
            return false;
        }

        return true;
    }

    protected getQueryTypeName(): string | undefined {
        return this.isManageWidgetMode() ?
            this.selectedWidgetRelayService.getCurrentNamedQuery()?.normalizeQueryTypeName() :
            this.widgetPrefs?.namedQuery?.normalizeQueryTypeName();
    }

    private isAdvancedGrid(): boolean {
        return !!this.preferences?.isAdvancedGrid?.();
    }

    private isDetailWidget(): boolean {
        return !!this.widgetPrefs?.isDetailWidget;
    }

    private isDynamicColumn(colId: string): boolean {
        const columnConfig = this.preferences?.configs?.values.find((value) => value.colId === colId);
        if (!this.metadata || !columnConfig || this.initialValues.find((v) => v.colId === columnConfig.colId)) {
            return false;
        }

        return !!this.metadata[columnConfig.value ?? ''].isDynamic;
    }

    private isGridConfigItemList(values: ValueConfigItem[]): values is GridConfigItem[] {
        return values.every((value) => this.isGridConfigItem(value));
    }
}
